import React, { createContext, useContext, useRef, useState, useEffect } from "react";

const RecordContext = createContext();

export const useRecord = () => useContext(RecordContext);

// Helper to check active recording from cookies
const getActiveRecordingFromCookies = () => {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  const isActive = getCookie('conversation_active') === 'true';
  const status = getCookie('recording_status');
  const conversationId = getCookie('conversation_id');
  return {
    isActive,
    status,
    conversationId,
    isLive: isActive && (status === '🎙️ Listening...' || status === 'Paused')
  };
};

function RecordProvider({ children }) {
  // All "persistent" states and refs go here
  const [status, setStatus] = useState('Ready');
  const [sessionId, setSessionId] = useState(null);
  const [shareLink, setShareLink] = useState('');
  const [noteTitle, setNoteTitle] = useState('Untitled Note');
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [persistentSegments, setPersistentSegments] = useState([]);
  const [paginationMeta, setPaginationMeta] = useState({
    totalDivs: 0,
    firstPageNumber: 0,
    lastPageNumber: 0,
    currentPageNumber: 0
  });
  const [timer, setTimer] = useState(0);
  const [dateString, setDateString] = useState('');
  const [timeString, setTimeString] = useState('');
  const [isPaused, setIsPaused] = useState(false);

  // Audio/WebSocket related refs
  const audioContextRef = useRef(null);
  const streamRef = useRef(null);
  const workletNodeRef = useRef(null);
  const assemblySocketRef = useRef(null);
  const socketRef = useRef(null);
  const timerRef = useRef(null);
  const waveformRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const isPausedRef = useRef(false);
  const [previousConversationId, setPreviousConversationId] = useState(null);
  const [conversationId, setConversationId] = useState(null);

  // MediaRecorder refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const [planCountdownEndsAt, _setPlanCountdownEndsAt] = useState(() => {
    if (typeof window === 'undefined') return null;
    const raw = sessionStorage.getItem('planCountdownEndsAt');
    if (!raw) return null;
    const ts = Number(raw);
    if (!Number.isFinite(ts) || ts <= Date.now()) {
      sessionStorage.removeItem('planCountdownEndsAt');
      return null;
    }
    return ts;
  });

  // Reprocess toggle state
  const [reprocessEnabled, setReprocessEnabled] = useState(false);

  const setPlanCountdownEndsAt = (ts) => {
    if (typeof window !== 'undefined') {
      if (ts && ts > Date.now()) {
        sessionStorage.setItem('planCountdownEndsAt', String(ts));
      } else {
        sessionStorage.removeItem('planCountdownEndsAt');
      }
    }
    _setPlanCountdownEndsAt(ts && ts > Date.now() ? ts : null);
  };

  useEffect(() => {
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };
  const isLive = getCookie('isLiveSession') === 'true';
  const sessionIdFromCookie = getCookie('liveSessionId');
  if (isLive && sessionIdFromCookie) {
    setStatus('🎙️ Listening...');
    setSessionId(sessionIdFromCookie);
  }
}, []);
  // Utility: is recording ongoing - check both state and cookies
  const isLive = (() => {
    // First check state - both listening and paused are considered live
    if (status === '🎙️ Listening...' || status === 'Paused') return true;
    
    // Fallback to cookies for session restoration scenarios
    const cookieState = getActiveRecordingFromCookies();
    return cookieState.isLive;
  })();

  // Ref to expose handleStop for external control (e.g., TopNavbar)
  const handleStopRef = useRef(null);

  // Add helper function for getCookie in context
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Helper to clear all session cookies when session is detected as stopped
  const clearAllSessionCookies = () => {
    const sessionCookies = [
      'recording_start_time',
      'recording_status',
      'is_recording_paused',
      'pause_start_time',
      'total_pause_duration',
      'last_activity_time',
      'original_session_id',
      'conversation_id',
      'conversation_active',
      'session_id',
      'transcription_token',
      'token_issued_at',
      'token_expires_at',
      'token_status',
      'isLiveSession',
      'liveSessionId'
    ];
    sessionCookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    });
    localStorage.removeItem('session_id');
    localStorage.removeItem('transcript_segments');
  
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('planCountdownEndsAt');
    }
  };

  // Helper: Reset context for new session
  const resetForNewSession = () => {
    const isConversationActive = document.cookie.includes('conversation_active=true');
    const isRecordingPaused = document.cookie.includes('is_recording_paused=true');
    const recordingStatus = getCookie('recording_status');
    const isLiveSessionCookie = getCookie('isLiveSession');
    const isLiveSession = isConversationActive ||
                         isRecordingPaused ||
                         recordingStatus === '🎙️ Listening...' ||
                         recordingStatus === 'Paused' ||
                         isLiveSessionCookie === 'true';

    // ❗ Do not reset if session is live
    if (isLiveSession) {
      return;
    }

    setTranscriptSegments([]);
    setTimer(0);
    setStatus('Initializing...');
    setIsPaused(false);
    setShareLink('');
    setDateString('');
    setTimeString('');
    setNoteTitle('Untitled Note');
    setPreviousConversationId(null);
    setConversationId(null);
    setPlanCountdownEndsAt(null);
    setReprocessEnabled(false); // Reset reprocess toggle for new sessions
    // Clear reprocess preference from localStorage for fresh start
    try {
      localStorage.removeItem('reprocess_enabled');
    } catch (e) {
      console.warn('[RecordContext] Failed to clear reprocess preference:', e?.message || e);
    }
  };

  // Helper: Reset context after session completed
  const resetAfterSessionCompleted = () => {
    setTranscriptSegments([]);
    setSessionId(null);
    setShareLink('');
    setTimer(0);
    setStatus('Ready');
    setDateString('');
    setTimeString('');
    setIsPaused(false);
    setNoteTitle('Untitled Note');
    setPreviousConversationId(null);
    setConversationId(null);
    setPlanCountdownEndsAt(null);
  };
  const resetAllRecordData = () => {
  // Check if there's an active live session before clearing all data
  const isConversationActive = getCookie('conversation_active') === 'true';
  const isRecordingPaused = getCookie('is_recording_paused') === 'true';
  const recordingStatus = getCookie('recording_status');
  const isLiveSessionCookie = getCookie('isLiveSession') === 'true';

  const isActiveSession = isConversationActive ||
                         isRecordingPaused ||
                         recordingStatus === '🎙️ Listening...' ||
                         recordingStatus === 'Paused' ||
                         isLiveSessionCookie;

  if (isActiveSession) {
    return;
  }
  
  setStatus('Ready');
  setSessionId(null);
  setShareLink('');
  setNoteTitle('Untitled Note');
  setTranscriptSegments([]);
  setPersistentSegments([]);
  setPaginationMeta({
    totalDivs: 0,
    firstPageNumber: 0,
    lastPageNumber: 0,
    currentPageNumber: 0
  });
  setTimer(0);
  setDateString('');
  setTimeString('');
  setIsPaused(false);
  setIsEditingTitle(false);
  isPausedRef.current = false;
  setPlanCountdownEndsAt(null);
};

  // Export all setters/getters
  return (
    <RecordContext.Provider value={{
      status, setStatus,
      sessionId, setSessionId,
      shareLink, setShareLink,
      noteTitle, setNoteTitle,
      transcriptSegments, setTranscriptSegments,
      persistentSegments,
      setPersistentSegments,
      paginationMeta,
      setPaginationMeta,
      timer, setTimer,
      dateString, setDateString,
      timeString, setTimeString,
      isPaused, setIsPaused,
      audioContextRef, streamRef, workletNodeRef,
      assemblySocketRef, socketRef, timerRef,
      waveformRef, analyserRef, animationFrameRef,
      isPausedRef, mediaRecorderRef, audioChunksRef,
      isLive,
      resetAllRecordData,
      isEditingTitle, setIsEditingTitle,
      handleStopRef,
      previousConversationId, setPreviousConversationId,
      conversationId, setConversationId,
      resetForNewSession,
      resetAfterSessionCompleted,
      clearAllSessionCookies,
      planCountdownEndsAt,
      setPlanCountdownEndsAt,
      reprocessEnabled, setReprocessEnabled,
    }}>
      {children}
    </RecordContext.Provider>
  );
}
export default RecordProvider;